import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';

const SLASH_MENU_KEY = new PluginKey('slashMenu');

// Groups show in this order; any other group follows, in first-seen order.
const GROUP_ORDER = ['Formatting', 'Blocks', 'Integrations'];

function groupRank(item) {
  const index = GROUP_ORDER.indexOf(item.group || 'Insert');
  return index === -1 ? GROUP_ORDER.length : index;
}

// Returns the slash command range if the cursor is immediately after a slash
// pattern (/query) that starts at a word boundary. Returns null otherwise.
function getSlashRange(state) {
  const { selection } = state;
  const { $from, empty } = selection;

  if (!empty || !$from.parent.isTextblock) return null;
  if ($from.parent.type.name === 'callout') return null;

  const textBefore = $from.parent.textContent.slice(0, $from.parentOffset);
  const match = textBefore.match(/\/([^\s]*)$/);
  if (!match) return null;

  const slashOffset = match.index;
  if (slashOffset > 0 && !/\s/.test(textBefore[slashOffset - 1])) return null;

  return {
    query: match[1],
    from: $from.pos - $from.parentOffset + slashOffset,
    to: $from.pos,
  };
}

class SlashMenuUI {
  constructor(items) {
    this.allItems = items;
    this.filteredItems = [];
    this.selectedIndex = 0;
    this.isOpen = false;
    this.range = null;
    this.editor = null;

    this.el = document.createElement('div');
    this.el.className = 'hidden fixed z-50 w-72 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden';
    this.el.innerHTML = `
      <div class="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
        <div class="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0">
          <span class="text-white text-sm leading-none">+</span>
        </div>
        <div class="text-sm text-gray-400">
          / <span data-slash-query class="text-gray-700 font-medium"></span><span data-slash-placeholder>insert...</span>
        </div>
      </div>
      <div data-slash-list class="overflow-y-auto max-h-80"></div>
    `;
    document.body.appendChild(this.el);
  }

  show(view, query, from, to) {
    this.range = { from, to };
    this.filteredItems = this._filter(query);

    this.el.querySelector('[data-slash-query]').textContent = query;
    this.el.querySelector('[data-slash-placeholder]').classList.toggle('hidden', query.length > 0);

    if (this.filteredItems.length === 0) {
      this.hide();
      return;
    }

    this.selectedIndex = Math.min(this.selectedIndex, this.filteredItems.length - 1);

    // Render and unhide so we can measure actual height before final positioning.
    this._render();
    this.isOpen = true;
    this.el.classList.remove('hidden');

    const { left, top: cursorTop, bottom: cursorBottom } = view.coordsAtPos(from);
    const menuHeight = this.el.offsetHeight;
    const viewportHeight = window.innerHeight;
    const margin = 8;

    // Position horizontally, clamped to viewport.
    const menuWidth = this.el.offsetWidth;
    this.el.style.left = `${Math.min(Math.max(margin, left), viewportHeight - menuWidth - margin)}px`;

    // Flip upward when there isn't enough space below the cursor.
    const spaceBelow = viewportHeight - cursorBottom - margin;
    if (spaceBelow >= menuHeight) {
      this.el.style.top = `${cursorBottom + 4}px`;
      this.el.style.bottom = '';
    } else {
      // Open upward from the cursor line.
      const topPos = cursorTop - menuHeight - 4;
      this.el.style.top = `${Math.max(margin, topPos)}px`;
      this.el.style.bottom = '';
    }
  }

  hide() {
    this.isOpen = false;
    this.selectedIndex = 0;
    this.el.classList.add('hidden');
  }

  selectNext() {
    this.selectedIndex = (this.selectedIndex + 1) % this.filteredItems.length;
    this._render();
  }

  selectPrev() {
    this.selectedIndex = (this.selectedIndex - 1 + this.filteredItems.length) % this.filteredItems.length;
    this._render();
  }

  confirm() {
    const item = this.filteredItems[this.selectedIndex];
    if (!item || !this.range || !this.editor) return;
    item.command({ editor: this.editor, range: this.range });

    // Ensure there's always a paragraph to continue writing after the inserted block.
    // We preserve the current selection so the cursor stays inside the inserted block
    // (e.g. inside a callout), but a blank paragraph exists below for when they're done.
    const { state } = this.editor;
    const { doc, selection } = state;
    const afterPos = selection.$to.after(1);
    if (afterPos >= doc.content.size) {
      const tr = state.tr.insert(doc.content.size, state.schema.nodes.paragraph.create());
      tr.setSelection(selection.map(tr.doc, tr.mapping));
      this.editor.view.dispatch(tr);
    }

    this.hide();
  }

  destroy() {
    this.el.remove();
  }

  _filter(query) {
    // Sorted here rather than in _render so arrow-key order matches what is on screen (the sort is stable).
    const available = this.allItems
      .filter(item => !item.canInsert || item.canInsert(this.editor))
      .sort((a, b) => groupRank(a) - groupRank(b));
    if (!query) return available;
    const q = query.toLowerCase();
    return available.filter(item =>
      item.title.toLowerCase().includes(q) ||
      (item.keywords || []).some(k => k.includes(q))
    );
  }

  _render() {
    const list = this.el.querySelector('[data-slash-list]');

    const groups = new Map();
    this.filteredItems.forEach((item, i) => {
      const g = item.group || 'Insert';
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g).push({ item, i });
    });

    list.innerHTML = [...groups.entries()].map(([group, entries]) => `
      <div class="px-3 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">${group}</div>
      ${entries.map(({ item, i }) => {
        const active = i === this.selectedIndex;
        return `
          <div class="flex items-center gap-3 px-3 py-2 cursor-pointer ${active ? 'bg-purple-100' : 'hover:bg-gray-50'}"
               data-slash-item="${i}">
            <div class="w-8 h-8 rounded-md border flex items-center justify-center flex-shrink-0 text-base
                        ${active ? 'border-indigo-400 bg-purple-100 text-black' : 'border-gray-200 bg-white text-gray-500'}">
              ${item.icon}
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-sm font-medium ${active ? 'text-black' : 'text-gray-900'}">${item.title}</div>
              ${item.description
                ? `<div class="text-xs ${active ? 'text-purple-900' : 'text-gray-400'}">${item.description}</div>`
                : ''}
            </div>
            ${item.shortcut
              ? `<div class="text-xs font-mono flex-shrink-0 ${active ? 'text-indigo-200' : 'text-gray-400'}">${item.shortcut}</div>`
              : ''}
          </div>
        `;
      }).join('')}
    `).join('');

    list.querySelectorAll('[data-slash-item]').forEach(el => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.selectedIndex = parseInt(el.dataset.slashItem, 10);
        this.confirm();
      });
    });

    list.querySelector(`[data-slash-item="${this.selectedIndex}"]`)?.scrollIntoView({ block: 'nearest' });
  }
}

export const SlashMenu = Extension.create({
  name: 'slashMenu',

  addOptions() {
    return { items: [] };
  },

  addProseMirrorPlugins() {
    const menu = new SlashMenuUI(this.options.items);
    menu.editor = this.editor;

    return [new Plugin({
      key: SLASH_MENU_KEY,

      view() {
        return {
          update(view) {
            const result = getSlashRange(view.state);
            if (result) {
              menu.show(view, result.query, result.from, result.to);
            } else {
              menu.hide();
            }
          },
          destroy() {
            menu.destroy();
          },
        };
      },

      props: {
        handleKeyDown(_view, event) {
          if (!menu.isOpen) return false;

          if (event.key === 'ArrowDown') { event.preventDefault(); menu.selectNext(); return true; }
          if (event.key === 'ArrowUp')   { event.preventDefault(); menu.selectPrev(); return true; }
          if (event.key === 'Enter')     { event.preventDefault(); menu.confirm();    return true; }
          if (event.key === 'Escape')    { menu.hide();                               return true; }

          return false;
        },
      },
    })];
  },
});
